import Vue from 'vue'
import axios from 'axios'
import router from '@/router'
import merge from 'lodash/merge'
import {clearLoginInfo} from '@/utils'
import HashUtils from '@/utils/crypto.js'
// 在文件顶部定义混淆秘钥
const SECRET_KEY = 'GfUSewa^tU4q%04$qnx9lE^CWGeGgICCojeH$cO%L%luGf8GlfzwwAwQj#5p&tVv' // 实际项目中应该从环境变量读取

const http = axios.create({
	timeout: 10000 * 30,
	withCredentials: true,
	headers: {
		'Content-Type': 'application/json; charset=utf-8'
	}
})

// 转换所有键值为字符串的辅助函数
const convertToStringParams = (params) => {
	// 如果参数不是对象或为null，直接转换为字符串
	if (typeof params !== 'object' || params === null) {
		const stringValue = String(params);
		// 处理"null"字符串转换为空字符串
		return stringValue === 'null' ? '' : stringValue;
	}

	// 如果是数组
	if (Array.isArray(params)) {
		return params.map(item => convertToStringParams(item));
	}

	// 如果是普通对象，按键排序处理
	const stringParams = {};
	const sortedKeys = Object.keys(params).sort(); // 对键进行排序
	for (const key of sortedKeys) {
		const value = params[key];
		if (Array.isArray(value)) {
			// 处理数组类型的值 - 递归处理数组中的每个元素
			stringParams[String(key)] = value.map(item => convertToStringParams(item));
		} else if (typeof value === 'object' && value !== null) {
			// 递归处理嵌套对象，并对嵌套对象也进行排序
			stringParams[String(key)] = convertToStringParams(value);
		} else {
			// 基本类型转换为字符串，并处理"null"字符串
			const stringValue = String(value);
			stringParams[String(key)] = stringValue === 'null' ? '' : stringValue;
		}
	}
	return stringParams
}
// 请求重试配置
const retryConfig = {
	retry: 2, // 重试次数
	retryDelay: 1000, // 重试间隔时间
	shouldRetry: (error) => {
		// 判断是否需要重试
		return [500, 502, 503, 504].includes(error.response.status)
	}
}
// 请求队列
const pendingRequests = new Map()

// 生成请求的唯一键
const generateRequestKey = (config) => {
	if (!config) {
		return ''
	}
	const {method, url, params, data} = config
	return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&')
}

// 添加请求到队列
const addPendingRequest = (config) => {
	if (!config) {
		return ''
	}
	const requestKey = generateRequestKey(config)
	config.cancelToken = config.cancelToken || new axios.CancelToken((cancel) => {
		if (!pendingRequests.has(requestKey)) {
			pendingRequests.set(requestKey, cancel)
		}
	})
}

// 从队列中移除请求
const removePendingRequest = (config) => {
	const requestKey = generateRequestKey(config)
	if (pendingRequests.has(requestKey)) {
		const cancel = pendingRequests.get(requestKey)
		cancel(requestKey)
		pendingRequests.delete(requestKey)
	}
}

/**
 * 请求拦截
 */
http.interceptors.request.use(config => {
	// 检查是否存在重复请求，若存在则取消已发的请求
	removePendingRequest(config)
	// 将当前请求添加到队列
	addPendingRequest(config)

	// 添加统一参数（不放在header中）
	const timestamp = Date.now().toString()
	const nonce = Math.random().toString(36).substring(2, 10) // 添加随机数防止重放攻击

	config.headers['token'] = Vue.cookie.get('uuid') // token仍然放在header中
	// 统一参数对象（不包括放在header中的参数）
	// 修改这里：将统一参数放入请求头
	config.headers['Time'] = timestamp
	config.headers['Nonce'] = nonce
	config.headers['ReqSource'] = 'pc-buyer'
	let sign = "";
	// POST/PUT请求签名计算
	if (config.data) {
		const stringifiedParams = convertToStringParams(config.data)
		sign = nonce + timestamp + JSON.stringify(stringifiedParams) + SECRET_KEY
		config.headers['Sign'] = HashUtils.md5HexHash(sign)
	}

// GET请求签名计算
	if (config.method === 'get') {
		let obj = getUrlQueryAll(config.url)
		if (config.url.includes('?')) {
			config.url = config.url.split('?')[0]
		}
		config.params = merge(obj, config.params)
		const stringifiedParams = convertToStringParams(config.params)
		sign = nonce + timestamp +  JSON.stringify(stringifiedParams) + SECRET_KEY
		config.headers['Sign'] = HashUtils.md5HexHash(sign)
	}
	return config
}, error => {
	return Promise.reject(error)
})

/**
 * 响应拦截
 */
http.interceptors.response.use(response => {
	// 从队列中移除请求
	removePendingRequest(response.config)

	if (response.data && response.data.status === -102) { // 401, token失效
		clearLoginInfo()
		router.push({ name: 'login' })
	}
	return response
}, error => {
	// 从队列中移除请求
	removePendingRequest(error.config)
	// 处理请求超时
	if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
		return handleRequestTimeout(error)
	}
	// 处理请求取消
	if (axios.isCancel(error)) {
		return Promise.reject(new Error('请求被取消'))
	}
	// 处理 HTTP 错误
	handleHttpError(error)
	// 尝试重试请求
	return handleRequestRetry(error)
})


const getUrlQueryAll = (url) => {
	const firstIndex = url.indexOf('?') + 1
	if (firstIndex === 0) {
		return {}
	}
	const queryStr = url.substring(firstIndex)
	if (!queryStr) {
		return {}
	}
	const queryArr = queryStr.split('&')
	const result = {}

	queryArr.forEach(item => {
		const [key, value] = item.split('=')
		if (key !== undefined) {
			result[decodeURIComponent(key)] = decodeURIComponent(value || '')
		}
	})
	return result
}
// 处理业务错误
const handleBusinessError = (res) => {
	// 处理特定的业务错误码
	switch (res.code) {
		case 401:
			// token 过期
			clearLoginInfo()
			router.push('/login')
			break
		case 403:
			// message.error('没有权限访问')
			break
		default:
		//  message.error(res.message || '操作失败')
	}
}

// 处理 HTTP 错误
const handleHttpError = (error) => {
	console.log(error)
	if (!error.response || !error.response.status) {
		return Promise.reject(error);
	}
	const status = error.response.status
	switch (status) {
		case 401:
			clearLoginInfo()
			router.push('/login')
			break
		case 403:
			//  message.error('没有权限访问')
			break
		case 404:
			//  message.error('请求的资源不存在')
			break
		case 500:
			//  message.error('服务器错误')
			break
		default:
		// message.error('网络错误')
	}
}

// 处理请求超时
const handleRequestTimeout = (error) => {
	//message.error('请求超时')
	return Promise.reject(error)
}

// 处理请求重试
const handleRequestRetry = async (error) => {
	const config = error.config

	// 判断是否配置了重试
	if (!config || !retryConfig.retry) {
		return Promise.reject(error)
	}

	// 设置重试次数
	config.__retryCount = config.__retryCount || 0

	// 检查是否应该重试
	if (!retryConfig.shouldRetry(error) || config.__retryCount >= retryConfig.retry) {
		return Promise.reject(error)
	}

	// 增加重试次数
	config.__retryCount += 1

	// 延时重试
	await new Promise(resolve => setTimeout(resolve, retryConfig.retryDelay))

	// 重试请求
	return request(config)
}

/**
 * 请求地址处理
 * @param {*} actionName action方法名称
 */
http.adornUrl = (actionName) => {
	// 非生产环境 && 开启代理, 接口前缀统一使用[/tao/]前缀做代理拦截!
	// return (process.env.NODE_ENV !== 'production' && process.env.OPEN_PROXY ? '/tao/' : window.SITE_CONFIG.baseUrl) + actionName
	return '/tao' + actionName
}

http.adornUrl2 = (actionName) => {
	// 非生产环境 && 开启代理, 接口前缀统一使用[/tao/]前缀做代理拦截!
	// return (process.env.NODE_ENV !== 'production' && process.env.OPEN_PROXY ? '/tao/' : window.SITE_CONFIG.baseUrl) + actionName
	return '/tao' + actionName
}
/**
 * get请求参数处理
 * @param {*} params 参数对象
 * @param {*} openDefultParams 是否开启默认参数?
 */
http.adornParams = (params = {}, openDefultParams = false) => {
	return params
}

/**
 * post请求数据处理
 * @param {*} data 数据对象
 * @param {*} openDefultdata 是否开启默认数据?
 * @param {*} contentType 数据格式
 *  json: 'application/json; charset=utf-8'
 *  form: 'application/x-www-form-urlencoded; charset=utf-8'
 */
http.adornData = (data = {}) => {
	return data
}

export default http



// WEBPACK FOOTER //
// ./src/utils/httpRequest.js